import React, { useState, useMemo, useEffect, useId } from "react";
import { Autosuggest, Select } from "@cloudscape-design/components";
import { debounce } from 'lodash';
import { useGetTopSearchDataV2 } from "./api/hooks/useGetTopSearchDataV2";
import { allVerticalOptions, convertData } from "./constants";
import { parseExpression } from "../../utils/filters/filterUtils";
import { Filter, FilterOperation, FilterType } from "../../types/filterTypes";

interface TitleSelectProps {
  verticals: string[];
  limit?: number;
  onSelect: (selectedOption: any) => void;
  onClear?: () => void;
  onVerticalChange?: (selectedVertical: any) => void;
  allowTextSubmit?: boolean;
  localStorageKey?: string;
  excludeIpIds?: string[];
}

const TitleSelect: React.FC<TitleSelectProps> = ({
  verticals,
  limit = 50,
  onSelect,
  onClear,
  onVerticalChange,
  allowTextSubmit=false,
  localStorageKey,
  excludeIpIds,
}) => {

  const verticalOptions = allVerticalOptions.filter(o => verticals.includes(o.value));
  const randomId = useId();

  const [query, setQuery] = useState(null);
  const [searchQuery, setSearchQuery] = useState(null);
  const [selectedVertical, setSelectedVertical] = useState(localStorageKey ? (verticalOptions.find(o => o.value === localStorage.getItem(localStorageKey)) ?? verticalOptions[0]) : verticalOptions[0]);
  const [currentTokenInfo, setCurrentTokenInfo] = useState({ start: 0, end: 0, token: null });
  
  const { data: searchResults, isLoading } = useGetTopSearchDataV2({
    q: searchQuery,
    verticals: selectedVertical.value === "all" ? verticalOptions.map(o => o.value).filter(v => v !== "all") : [selectedVertical.value],
    limit: 50,
  });

  const debouncedSetSearchQuery = useMemo(() =>
    debounce((value) => {
      setSearchQuery(value);
    }, 800), []);

  const getCursorPosition = () => {
    const input = document.getElementById(randomId)?.querySelector("input");
    if (!input) return 0;

    return input.selectionEnd;
  };

  const getEditingExpression = (filteringText: string) => {
    const cursorPosition = getCursorPosition();
    const textFilter = {
      filterType: FilterType.Text,
      colId: "ip",
      type: FilterOperation.Contains,
      filter: filteringText,
    };
    const conditions = parseExpression(filteringText, textFilter as any);

    const getNestedConditions = (cond: any) => {
      if (cond.filterType === FilterType.Join) {
        return cond.conditions.map(getNestedConditions).flat();
      }
      return [cond];
    };
    
    const tokens = getNestedConditions(conditions);
    const currentToken = tokens.find(token => filteringText.indexOf(token.filter) <= cursorPosition && filteringText.indexOf(token.filter) + token.filter.length >= cursorPosition);

    if (currentToken) {
      const start = filteringText.indexOf(currentToken.filter);
      const end = start + currentToken.filter.length;
      setCurrentTokenInfo({ start, end, token: currentToken.filter });
      return currentToken.filter;
    }

    return filteringText;
  };

  const replaceCurrentToken = (filteringText: string, newValue: string) => {
    const start = currentTokenInfo.start;
    const end = currentTokenInfo.end;

    if (currentTokenInfo.token.startsWith("\"")) {
      newValue = `"${newValue}"`;
    }

    const newFilteringText = filteringText.slice(0, start) + newValue + filteringText.slice(end);
    setQuery(newFilteringText);
    setCurrentTokenInfo({ start, end: start + newValue.length, token: newValue });
    
    return newFilteringText;
  };

  useEffect(() => {
    return () => debouncedSetSearchQuery.cancel();
  }, [debouncedSetSearchQuery]);

  return (
    <div className="relative w-full flex">
      <div className="[&_button]:!rounded-se-none [&_button]:!rounded-ee-none [&_button]:!border-r-0">
        <Select
          options={verticalOptions as any}
          selectedOption={selectedVertical as any}
          onChange={({ detail }) => {
            setSelectedVertical(detail.selectedOption as any);
            onVerticalChange?.(detail.selectedOption.value);
            if (localStorageKey) {
              localStorage.setItem(localStorageKey, detail.selectedOption.value);
            }
            setTimeout(() => {
              document.querySelector('[class^="topsearch-input-container"]')?.querySelector('input')?.setAttribute("tabindex", "0");
              document.querySelector('[class^="topsearch-input-container"]')?.querySelector('input')?.focus();
            }, 0);
          }}
          disabled={verticalOptions.length === 1}
          expandToViewport
        />
      </div>
      <div className="topsearch-input-container grow [&_input]:!rounded-ss-none [&_input]:!rounded-es-none" id={randomId}>
        <Autosuggest
          onChange={({ detail }) => {
            setQuery(detail.value);
            if (detail.value.length === 0) {
              onClear?.();
            }
          }}
          onLoadItems={({ detail }) => debouncedSetSearchQuery(getEditingExpression(detail.filteringText))}
          onSelect={({ detail }) => {
            if (detail.selectedOption) {
              const newFilteringText = replaceCurrentToken(query, detail.selectedOption.label);
              onSelect({ ...detail.selectedOption, filteringText: newFilteringText });
            } else if (allowTextSubmit) {
              setQuery(detail.value);
              onSelect({ label: detail.value, value: detail.value, filteringText: detail.value });
            }
          }}
          expandToViewport
          value={query}
          options={ (searchResults && searchResults?.results?.length > 0) ? convertData(searchResults.results).map(item => ({ ...item, disabled: excludeIpIds?.includes(item.value) })) : [] }
          placeholder="Search"
          filteringType="none"
          empty={query?.length > 0 ? "No results found" : null}
          statusType={isLoading ? "loading" : "finished"}
          enteredTextLabel={(value) => `Searching for "${value}"` + (selectedVertical.value !== "all" ? ` in ${selectedVertical.label.toLocaleLowerCase()}` : "")}
        />
      </div>
    </div>
  );
};

export default TitleSelect;